import { useState } from 'react';
import type { MouseEvent } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import { CircularProgress } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import * as Sentry from '@sentry/react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface Props {
  url: string;
  width?: number;
}

const PDFViewer: React.FC<Props> = ({ url, width = 500 }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.1, 2));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.1, 0.5));

  return (
    <MDBox
      border="1px solid"
      borderColor="grey.300"
      onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      {/* Controls Header */}
      <MDBox
        p={1}
        borderBottom="1px solid"
        borderColor="grey.300"
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bgcolor="grey.50"
        sx={{ minHeight: '40px' }}
      >
        {/* Page Navigation */}
        <MDBox display="flex" alignItems="center">
          <MDBox
            component="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setPageNumber(Math.max(1, pageNumber - 1));
            }}
            disabled={pageNumber <= 1}
            mx={0.5}
            px={1.5}
            py={0.5}
            bgcolor="grey.100"
            border="none"
            borderRadius="borderRadius"
            sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
          >
            Previous
          </MDBox>
          <MDTypography variant="caption" mx={1}>
            Page {pageNumber} of {numPages || '?'}
          </MDTypography>
          <MDBox
            component="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              setPageNumber(Math.min(numPages || 1, pageNumber + 1));
            }}
            disabled={!numPages || pageNumber >= numPages}
            mx={0.5}
            px={1.5}
            py={0.5}
            bgcolor="grey.100"
            border="none"
            borderRadius="borderRadius"
            sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
          >
            Next
          </MDBox>
        </MDBox>

        {/* Zoom Controls */}
        <MDBox display="flex" alignItems="center">
          <MDBox
            component="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              zoomOut();
            }}
            mx={0.5}
            px={1.5}
            py={0.5}
            bgcolor="grey.100"
            border="none"
            borderRadius="borderRadius"
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ZoomOutIcon sx={{ fontSize: '1rem' }} />
          </MDBox>
          <MDTypography variant="caption" mx={1}>
            {Math.round(scale * 100)}%
          </MDTypography>
          <MDBox
            component="button"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              zoomIn();
            }}
            mx={0.5}
            px={1.5}
            py={0.5}
            bgcolor="grey.100"
            border="none"
            borderRadius="borderRadius"
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ZoomInIcon sx={{ fontSize: '1rem' }} />
          </MDBox>
        </MDBox>
      </MDBox>

      {/* PDF Content */}
      <MDBox p={2} sx={{ overflowX: 'auto' }}>
        <Document
          key={url}
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="400px"
            >
              <CircularProgress />
            </MDBox>
          }
          onLoadError={(err) => {
            Sentry.captureException(err);
            console.error(err);
          }}
          error={
            <MDBox textAlign="center" py={2}>
              <MDTypography color="error">
                Error loading PDF. Please try downloading instead.
              </MDTypography>
            </MDBox>
          }
        >
          {({ pdf }) => {
            const totalPages = numPages ?? pdf?.numPages ?? 0;

            if (!totalPages) {
              return (
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="400px"
                >
                  <CircularProgress size={24} />
                </MDBox>
              );
            }

            return Array.from({ length: totalPages }, (_, index) => (
              <Page
                key={index}
                pageNumber={index + 1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={width * scale}
                loading={
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="400px"
                  >
                    <CircularProgress size={24} />
                  </MDBox>
                }
              />
            ));
          }}
        </Document>
      </MDBox>
    </MDBox>
  );
};

export default PDFViewer;
